<template>
  <v-app id="app" class="wt-app">
    <!-- ============== TOP INFO STRIP ============== -->
    <div class="wt-topstrip">
      <div class="wt-container wt-topstrip__row">
        <span>
          <strong>die ZZV-Sprechstunde der WaizmannTabelle</strong>
          <span class="wt-divider wt-topstrip__long">|</span>
          <span class="wt-topstrip__long">kostenlos &amp; unverbindlich</span>
        </span>
        <span class="wt-topstrip__right">
          <span class="wt-topstrip__long">Telefonberatung<span class="wt-divider">|</span></span>
          <span>ca. 10–15&nbsp;Min.</span>
        </span>
      </div>
    </div>

    <!-- ============== MAIN HEADER ============== -->
    <header class="wt-header">
      <div class="wt-container wt-header__row">
        <router-link to="/" class="wt-logo">
          <span class="wt-logo__main">Waizmann<span class="wt-logo__suffix">.089</span></span>
          <span class="wt-logo__tag">die ZZV-Sprechstunde der WaizmannTabelle<sup class="wt-logo__reg">®</sup></span>
        </router-link>

        <div class="wt-header__cta">
          <a href="#kalender" class="wt-btn wt-btn--cta">
            Termin sichern
          </a>
        </div>
      </div>
    </header>

    <main class="wt-main">
      <router-view />
    </main>

    <footer class="wt-footer">
      <div class="wt-container wt-footer__inner">
        <div class="wt-footer__brand">
          <span class="wt-logo">
            <span class="wt-logo__main">Waizmann<span class="wt-logo__suffix">.089</span></span>
          </span>
          <p class="wt-footer__tag">
            die ZZV-Sprechstunde der WaizmannTabelle<sup>®</sup>
          </p>
        </div>

        <div class="wt-footer__bottom">
          <span>&copy; {{ year }} Waizmann.089 · ein Service der WaizmannTabelle<sup>®</sup></span>
          <nav class="wt-footer__links">
            <router-link to="/impressum">Impressum</router-link>
          </nav>
        </div>
      </div>
    </footer>
  </v-app>
</template>

<script setup>
import { computed } from "vue";
const year = computed(() => new Date().getFullYear());
</script>

<style>
/* =========================================================
   DESIGN TOKENS – WaizmannTabelle Style
   ========================================================= */
:root {
  --wt-blue: #2c5b9e;
  --wt-blue-deep: #1a3d6b;
  --wt-blue-soft: #e5edf4;
  --wt-blue-line: #d6e1ec;
  --wt-red: #e54c39;
  --wt-yellow: #fecf0c;
  --wt-green: #4caf50;
  --wt-ink: #1a1a1a;
  --wt-text: #333333;
  --wt-muted: #6b7480;
  --wt-bg: #ffffff;
  --wt-shadow: 0 2px 12px rgba(26, 61, 107, 0.08);

  --wt-container: 1240px;
  --wt-header-h: 60px;
  --wt-topstrip-h: 30px;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
}
*, *::before, *::after { box-sizing: border-box; }

.wt-app,
.wt-app .v-application__wrap {
  background: var(--wt-bg) !important;
  color: var(--wt-text);
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
}

.wt-app a {
  color: var(--wt-blue);
  text-decoration: none;
}
.wt-app a:hover { color: var(--wt-blue-deep); }

.wt-container {
  max-width: var(--wt-container);
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
}

.wt-divider {
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.45);
}

/* =========================================================
   TOP STRIP
   ========================================================= */
.wt-topstrip {
  background: var(--wt-blue-deep);
  color: #fff;
  height: var(--wt-topstrip-h);
  font-size: 12px;
  letter-spacing: 0.2px;
}
.wt-topstrip__row {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wt-topstrip__right { opacity: 0.9; }
.wt-topstrip strong { font-weight: 700; }

@media (max-width: 600px) {
  .wt-topstrip__long { display: none; }
}

/* =========================================================
   HEADER
   ========================================================= */
.wt-header {
  background: #fff;
  border-bottom: 2px solid var(--wt-blue);
  position: sticky;
  top: 0;
  z-index: 50;
}
.wt-header__row {
  height: var(--wt-header-h);
  display: flex;
  align-items: center;
  gap: 20px;
}
.wt-logo {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  text-decoration: none;
  color: var(--wt-ink) !important;
  flex-shrink: 0;
  line-height: 1;
  gap: 2px;
}
.wt-logo__main {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.5px;
  line-height: 1;
  color: var(--wt-ink);
}
.wt-logo__suffix {
  color: var(--wt-blue);
  font-weight: 900;
}
.wt-logo__reg {
  font-size: 9px;
  font-weight: 700;
  color: var(--wt-blue);
  margin-left: 1px;
}
.wt-logo__tag {
  font-size: 11px;
  font-weight: 600;
  color: var(--wt-muted);
  letter-spacing: 0.1px;
  white-space: nowrap;
}

.wt-header__cta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.wt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 18px;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.2px;
  border-radius: 0;
  border: 2px solid var(--wt-blue);
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none !important;
  font-family: inherit;
}
.wt-btn--cta {
  height: 38px;
  padding: 0 18px 0 14px;
  background: var(--wt-blue);
  border: 2px solid var(--wt-blue);
  border-left: 4px solid var(--wt-yellow);
  color: #fff !important;
  font-weight: 800;
  letter-spacing: 0.1px;
  white-space: nowrap;
}

/* ----- „Das Original seit 1995"-Marke (blau, weiße Schrift) ----- */
.wt-original {
  background: var(--wt-blue);
  color: #fff;
  padding: 20px 18px 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  aspect-ratio: 1 / 1.6;
  width: 240px;
  gap: 10px;
}
.wt-original__top {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
}
.wt-original__big {
  font-size: 40px;
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -1.6px;
}
.wt-original__seit {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.8px;
}
.wt-original__no {
  font-size: 60px;
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -2px;
}
.wt-btn--cta:hover {
  background: var(--wt-blue-deep);
  border-color: var(--wt-blue-deep);
  border-left-color: var(--wt-yellow);
  color: #fff !important;
}

@media (max-width: 720px) {
  .wt-logo__main { font-size: 19px; }
  .wt-logo__tag { font-size: 10px; white-space: normal; max-width: 200px; line-height: 1.25; }
  .wt-btn { height: 34px; padding: 0 12px; font-size: 12px; }
}
@media (max-width: 460px) {
  .wt-logo__tag { display: none; }
}

/* =========================================================
   MAIN
   ========================================================= */
.wt-main {
  min-height: 60vh;
}

/* =========================================================
   FOOTER
   ========================================================= */
.wt-footer {
  background: var(--wt-blue-deep);
  color: #cbd5e1;
  padding: 36px 0 20px;
  margin-top: 60px;
}
.wt-footer__inner {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.wt-footer .wt-logo,
.wt-footer .wt-logo__main {
  color: #fff !important;
}
.wt-footer .wt-logo__suffix {
  color: var(--wt-yellow);
}
.wt-footer__tag {
  margin-top: 12px;
  color: #cbd5e1;
  font-size: 14px;
}
.wt-footer__tag sup {
  font-size: 9px;
  color: var(--wt-yellow);
}
.wt-footer__bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 16px;
  color: #94a3b8;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.wt-footer__links a {
  color: #cbd5e1;
  text-decoration: none;
  font-weight: 700;
}
.wt-footer__links a:hover {
  color: #fff;
  text-decoration: underline;
}
</style>
